import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import useCacheMutation from "~hooks/useCacheMutation";
import useOnlineStatus from "~hooks/useOnlineStatus";
import {
  atualizarEmissor,
  getCarteiraClientes,
  getCarteiraProspects,
  getCarteiraIdClientes,
  getCarteiraIdProspects,
  getNaoCarteiraIdClientes,
  getCliente,
  getProspect,
  getSolicitations,
  salvarLimite,
  salvarProspect,
  atualizarProspect,
  vincularProspect,
  salvarEmissor,
  salvarRecebedor,
  salvarBuscaOnline,
  atualizarRecebedor,
  getClienteCreditos,
  setInativarProspect,
  salvarConversao,
  getReenviarSolicitacao,
  getClienteCarteira,
  getProspectCarteira,
  buscaCep,
  getDocumentos,
  buscaCeps,
  buscaAllCeps,
  buscaOnline,
} from "~services/WalletServices";

export const useGetSolicitations = (vendedorId = null) => {
  const { data, error, isLoading, isSuccess } = useQuery(["solicitacoes", vendedorId], () => getSolicitations(vendedorId));

  return { data, error, isLoading, isSuccess };
};

export const useReenvioSolicitacao = (idSolicitacao, enableReenviarSolicitacao) => {
  const { data, error, isLoading, isSuccess } = useQuery(
    ["reenviarSolicitacao", idSolicitacao],
    () => getReenviarSolicitacao(idSolicitacao),
    {
      enabled: enableReenviarSolicitacao,
    }
  );

  return { data, error, isLoading, isSuccess };
};

export const useBuscaCEP = (cep, enable, handleError) => {
  const { data, error, isLoading, isSuccess } = useQuery(["ceps", cep], () => buscaCep(cep), {
    enabled: enable,
    onError: handleError,
  });

  return { data, error, isLoading, isSuccess };
};

export const useBuscaOnline = (busca, enable) => {
  const { data, error, isLoading, isSuccess, isFetching, isRefetching } = useQuery(["ceps", busca], () => buscaOnline(busca), {
    enabled: enable,
  });

  return { data: data?.data, error, isLoading, isSuccess, isFetching, isRefetching };
};

export const useBuscaCEPs = (enable, take, skip, dataAtualizacao) => {
  const cepRequest = useQuery(["cepsVendedor"], () => buscaCeps(skip, take, dataAtualizacao), {
    enabled: enable,
  });

  const queryClient = useQueryClient();

  const clearCache = () => {
    queryClient.removeQueries(["cepsVendedor"]);
  };

  return { ...cepRequest, clearCache };
};

export const useBuscaAllCEPs = (enable, take, skip, estado, dataAtualizacao) => {
  const { data, error, isLoading, isSuccess, refetch } = useQuery(["all-ceps", estado], () => buscaAllCeps(skip, take, estado, dataAtualizacao), {
    enabled: enable,
  });

  return { data, error, isLoading, isSuccess, refetch };
};

export const useSalvarProspect = (camposProspect, onSuccessCloseModal, onErrorCloseModal) => {
  delete camposProspect.microrregiao;
  delete camposProspect.mesorregiao;
  delete camposProspect.grupoSegmento;

  const { mutateCached } = useCacheMutation(
    camposProspect.id ? atualizarProspect : salvarProspect,
    camposProspect.id ? "Atualizar Prospect" : "Salvar Prospect",
    null,
    null,
    camposProspect.id ? "Enviado Atualizar Prospect" : "Enviado Salvar Prospect",
    onSuccessCloseModal,
    onErrorCloseModal
  );

  const handleSave = () => {
    mutateCached(camposProspect);
  };

  return handleSave;
};

export const useVincularProspect = (onSuccessCloseModal, onErrorCloseModal) => {
  const { mutateCached } = useCacheMutation(
    vincularProspect,
    "Vincular Prospect",
    null,
    null,
    "Vinculo adicionado com sucesso!",
    onSuccessCloseModal,
    onErrorCloseModal
  );

  const handleSave = (camposProspect) => {
    mutateCached(camposProspect);
  };

  return handleSave;
};

export const useSalvarEmissor = (camposEmissor, onSuccessCloseModal, onErrorCloseModal) => {
  let mutationFunction, mutationKey;

  if (camposEmissor.id) {
    if (camposEmissor.conversao) {
      mutationFunction = salvarConversao;
      mutationKey = "Salvar Conversão";
    } else {
      mutationFunction = atualizarEmissor;
      mutationKey = "Atualizar Emissor";
    }
  } else {
    mutationFunction = salvarEmissor;
    mutationKey = "Salvar Emissor";
  }

  const { mutateCached } = useCacheMutation(
    mutationFunction,
    mutationKey,
    onSuccessCloseModal,
    onErrorCloseModal,
    "Verifique o status na aba minhas solicitações"
  );

  const handleSave = () => {
    mutateCached(camposEmissor);
  };
  return handleSave;
};

export const useSalvarRecebedor = (camposRecebedor, onSuccessCloseModal, onErrorCloseModal) => {
  const { mutateCached } = useCacheMutation(
    camposRecebedor.id ? atualizarRecebedor : salvarRecebedor,
    camposRecebedor.id ? "Atualizar Recebedor" : "Salvar Recebedor",
    onSuccessCloseModal,
    onErrorCloseModal,
    "Verifique o status na aba minhas solicitações"
  );

  const handleSave = () => {
    mutateCached(camposRecebedor);
  };

  return handleSave;
};

export const useSalvarBuscaOnline = (campos, onSuccessCloseModal, onErrorCloseModal) => {
  const { mutateCached } = useCacheMutation(
    salvarBuscaOnline,
    "Salvar Busca Online",
    onSuccessCloseModal,
    onErrorCloseModal,
    "Verifique o status na aba minhas solicitações"
  );

  const handleSave = () => {
    mutateCached(campos);
  };

  return handleSave;
};

export const useSalvarLimiteCredito = (camposLimite, onSuccessCloseModal, onErrorCloseModal) => {
  const { mutateCached } = useCacheMutation(
    salvarLimite,
    "Salvar Limite",
    onSuccessCloseModal,
    onErrorCloseModal,
    "Verifique o status na aba minhas solicitações"
  );

  const handleSave = () => {
    mutateCached(camposLimite);
  };

  return handleSave;
};

export const useGetCarteiraClientes = (take, skip, enabled, vendedorId = null) => {
  const queryClient = useQueryClient();

  const clientes = useQuery(["carteiraClientes", take, skip, vendedorId], () => getCarteiraClientes(take, skip, vendedorId), {
    staleTime: 1000,
    enabled,
    retry: 0,
  });


  const { total, data } = clientes?.data || {};

  const carteira = {
    data: data?.carteira,
    ids: data?.carteiraIds,
    total,
    error: clientes.error,
    isFetching: clientes.isFetching,
    isLoading: clientes.isLoading,
    isSuccess: clientes.isSuccess,
    refetch: clientes.refetch,
  };

  const clearCache = () => {
    queryClient.removeQueries(["carteiraClientes", take, skip]);
  };

  return { ...carteira, clearCache };
};

export const useGetCarteiraProspects = (take, skip, vendedorId = null, enabled = true) => {
  const queryProspect = useQueryClient();

  const prospects = useQuery(["carteiraProspects", take, skip, vendedorId], () => getCarteiraProspects(take, skip, vendedorId), {
    staleTime: 1000,
    enabled,
  });

  const { total, data } = prospects?.data || {};

  const carteira = {
    data,
    total,
    error: prospects.error,
    isFetching: prospects.isFetching,
    isLoading: prospects.isLoading,
    isSuccess: prospects.isSuccess,
    refetch: prospects.refetch,
  };

  const clearCache = () => {
    queryProspect.removeQueries(["carteiraProspects", take, skip]);
  };

  return { ...carteira, clearCache };
};

// export const useGetDocumentos = (take, enabled) => {
//   const documentos = useQuery(["documentos", take], () => getDocumentos(take), {
//     enabled: enabled,
//   });
//   return documentos;
// };

export const useGetCarteiraIdsClientes = () => {
  const clientes = useQuery(["carteiraIdsClientes"], () => getCarteiraIdClientes());

  return clientes;
};

export const useGetCarteiraIdsProspects = () => {
  const prospects = useQuery(["carteiraIdsProspects"], () => getCarteiraIdProspects());

  return prospects;
};

export const useGetNaoCarteiraIdsClientes = () => {
  const clientes = useQuery(["carteiraNaoIdsClientes"], () => getNaoCarteiraIdClientes());

  return clientes;
};

export const useGetClienteCarteira = (id, enabled) => {
  const clientes = useQuery(["clienteCarteira", id], () => getClienteCarteira(id), {
    enabled: enabled,
    cacheTime: 0
  });

  return clientes;
};

export const useGetProspectCarteira = (id, enabled) => {
  const prospect = useQuery(["prospectCarteira", id], () => getProspectCarteira(id), {
    enabled: enabled,
    cacheTime: 0
  });

  return prospect;
};

export const useGetCliente = (id, enable) => {
  const res = useQuery(["cliente", id], () => getCliente(id), {
    enabled: enable,
  });

  return res;
};

export const useGetCreditosCliente = (id = 0, enabled = false) => {
  const creditos = useQuery(["clienteCreditos", id], () => getClienteCreditos(id), {
    enabled: enabled,
  });
  return creditos;
};

export const useSetInativarProspect = (id = 0, enable) => {
  const creditos = useQuery(["prospectInativado", id], () => setInativarProspect(id), {
    enabled: enable,
  });
  return creditos;
};
